import React, { useEffect, useState } from "react";
import { IRegisterRestaurantUser, registerRestaurantUser, postRestaurantUser } from "../services/auth";
import { getRestaurantList } from "../services/restaurant";
import { IRestaurantDetail } from "../models/IRestaurantDetail";

const DEFAULT_ROLE_ID = "0cbe4ee5-2b6c-ef11-adfb-5bef2cd6e8d8";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const emptyForm: IRegisterRestaurantUser = {
  customerName: "",
  customerSurName: "",
  customerUserName: "",
  customerTelNo: "",
  customerEmail: "",
  customerBirthDate: new Date().toISOString(),
  customerIsEnabled: true,
  customerLogin: { customerPassword: "" },
};

export default function CreateUserModal({ onClose, onSuccess }: Props) {
  const [form, setForm] = useState<IRegisterRestaurantUser>(emptyForm);
  const [birthDate, setBirthDate] = useState("");
  const [restaurants, setRestaurants] = useState<IRestaurantDetail[]>([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    getRestaurantList()
      .then((res) => setRestaurants(res.data.items ?? []))
      .catch(() => {});
  }, []);

  const set = (field: keyof IRegisterRestaurantUser, value: any) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRestaurantId) {
      setError("Lütfen bir restoran seçin.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload: IRegisterRestaurantUser = {
        ...form,
        customerBirthDate: birthDate
          ? new Date(birthDate).toISOString()
          : new Date().toISOString(),
      };

      // 1. Kullanıcıyı oluştur
      const userRes = await registerRestaurantUser(DEFAULT_ROLE_ID, payload);
      const customerId = userRes.data?.id ?? (userRes.data as any)?.customerId;
      if (!customerId) {
        setError("Kullanıcı oluşturuldu fakat ID alınamadı. Lütfen tekrar deneyin.");
        setLoading(false);
        return;
      }

      // 2. Restorana ata
      try {
        await postRestaurantUser(customerId, selectedRestaurantId);
      } catch (assignErr: any) {
        const msg = assignErr?.response?.data?.message ?? assignErr?.response?.data ?? "Restoran ataması başarısız oldu.";
        setError(`Kullanıcı oluşturuldu (ID: ${customerId}) fakat restorana atanamadı: ${msg}`);
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = Array.isArray(data) && data[0]?.description
        ? data[0].description
        : data?.message ?? data ?? "Kullanıcı oluşturulurken bir hata oluştu.";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={!loading ? onClose : undefined}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(4px)", zIndex: 1040,
        }}
      />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        background: "#fff", borderRadius: 20, zIndex: 1050,
        width: "min(560px, 95vw)", maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)", padding: "28px",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <h5 style={{ margin: 0, fontWeight: 800, color: "#111827" }}>
            <i className="bi bi-person-plus-fill me-2" style={{ color: "#6366f1" }}></i>
            Yeni Kullanıcı Oluştur
          </h5>
          {!loading && (
            <button onClick={onClose} style={{
              background: "#f3f4f6", border: "none", borderRadius: "50%",
              width: 36, height: 36, cursor: "pointer", color: "#6b7280",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem",
            }}>
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>

        {/* Success */}
        {success && (
          <div style={{
            background: "#dcfce7", border: "1px solid #86efac", borderRadius: 12,
            padding: "16px 20px", color: "#16a34a",
            display: "flex", alignItems: "center", gap: 10, fontWeight: 600,
          }}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: "1.2rem" }}></i>
            Kullanıcı başarıyla oluşturuldu!
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
            padding: "12px 16px", color: "#dc2626",
            display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: "0.85rem",
          }}>
            <i className="bi bi-exclamation-triangle-fill"></i> {String(error)}
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <FormField label="Ad *" required>
                <input
                  required
                  placeholder="Ad"
                  value={form.customerName}
                  onChange={(e) => set("customerName", e.target.value)}
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Soyad *" required>
                <input
                  required
                  placeholder="Soyad"
                  value={form.customerSurName}
                  onChange={(e) => set("customerSurName", e.target.value)}
                  style={inputStyle}
                />
              </FormField>

              <FormField label="E-posta *" required>
                <input
                  required
                  type="email"
                  placeholder="ornek@email.com"
                  value={form.customerEmail}
                  onChange={(e) => {
                    set("customerEmail", e.target.value);
                    set("customerUserName", e.target.value);
                  }}
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Telefon">
                <input
                  placeholder="+905XXXXXXXXX"
                  value={form.customerTelNo}
                  onChange={(e) => set("customerTelNo", e.target.value)}
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Doğum Tarihi">
                <input
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                  style={inputStyle}
                />
              </FormField>

              <FormField label="Şifre *" required style={{ gridColumn: "1 / -1" }}>
                <div style={{ position: "relative" }}>
                  <input
                    required
                    type={showPassword ? "text" : "password"}
                    placeholder="Şifre"
                    value={form.customerLogin.customerPassword}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, customerLogin: { customerPassword: e.target.value } }))
                    }
                    style={{ ...inputStyle, paddingRight: 38 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    style={{
                      position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer",
                      color: "#9ca3af", padding: 0, fontSize: "1rem",
                    }}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                </div>
              </FormField>

              <FormField label="Restoran *" required style={{ gridColumn: "1 / -1" }}>
                <select
                  required
                  value={selectedRestaurantId}
                  onChange={(e) => setSelectedRestaurantId(e.target.value)}
                  style={{ ...inputStyle, background: "#fff" }}
                >
                  <option value="">-- Restoran Seçin --</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.restourantName}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: "10px 20px", borderRadius: 10, border: "1.5px solid #e5e7eb",
                  background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer",
                  fontSize: "0.88rem",
                }}
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: "10px 24px", borderRadius: 10, border: "none",
                  background: loading ? "#a5b4fc" : "linear-gradient(135deg, #6366f1, #4f46e5)",
                  color: "#fff", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "0.88rem", display: "flex", alignItems: "center", gap: 8,
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    İşleniyor...
                  </>
                ) : (
                  <><i className="bi bi-person-check"></i> Kullanıcı Oluştur</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "9px 12px",
  border: "1.5px solid #e5e7eb", borderRadius: 10,
  fontSize: "0.87rem", outline: "none", boxSizing: "border-box",
};

function FormField({
  label, children, required, style
}: {
  label: string; children: React.ReactNode; required?: boolean; style?: React.CSSProperties;
}) {
  return (
    <div style={style}>
      <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
